import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../../services/loggingService';

// Base URL from API documentation
// Using api.innoworx.ai to avoid CORS issues (documentation shows x1f3lmpgxa.execute-api...)
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      // Trim token to remove any whitespace that might cause header parsing issues
      const cleanedToken = idToken.trim();
      return cleanedToken;
    }
    
    console.warn('Closing Stock API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Closing Stock API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

      if (!response.ok) {
        const responseText = await response.text();
        let errorData = {};
        try {
          errorData = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
          console.warn('Could not parse error response as JSON:', e);
          errorData = { message: responseText || 'Unknown error' };
        }
        
        console.error(
          `Closing Stock API Error: ${response.status} ${response.statusText} for ${endpoint}`,
          errorData
        );

        // Provide more helpful error messages for common status codes
        let errorMessage = 'Unknown error';
        if (response.status === 502) {
          errorMessage = 'Bad Gateway - The backend service may be unavailable or experiencing issues. Please try again later or contact support.';
        } else if (response.status === 503) {
          errorMessage = 'Service Unavailable - The service is temporarily unavailable. Please try again later.';
        } else if (response.status === 500) {
          errorMessage = 'Internal Server Error - An error occurred on the server. Please check the request format and try again.';
        } else if (response.status === 400) {
          errorMessage = errorData.body?.error || errorData.error || errorData.message || 'Bad Request - Please check your input data.';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized - Please log in again.';
        } else {
          errorMessage = errorData.body?.error || errorData.error || errorData.message || `Request failed with status ${response.status}`;
        }

        const error = new Error(`API Error: ${response.status} - ${errorMessage}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = endpoint;
        error.responseData = errorData;

        // Log API error to logging service
        loggingService.logError(
          'api_error_closing_stock',
          `Closing Stock API error: ${response.status} ${response.statusText} for ${endpoint}`,
          error,
          {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData: responseText,
          }
        ).catch(logError => {
          console.warn('[closingStockApi] Failed to log error:', logError);
        });

        throw error;
      }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      // Log authentication error to logging service
      loggingService.logError(
        'api_error_closing_stock',
        `Closing Stock API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[closingStockApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.error(`Closing Stock API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;

    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_closing_stock',
      `Closing Stock API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[closingStockApi] Failed to log error:', logError);
    });

    throw error;
  }
};

/**
 * API utility functions for Closing Stock Management.
 */
export const closingStockApi = {
  /**
   * Retrieves closing stock snapshot with per-item balances, summary KPIs, and optional opening stock calculation.
   * GET /stock/closing
   * Lambda: innoworx-stock-get-closing
   * 
   * @param {Object} params - Optional parameters for filtering and pagination
   * @param {number} [params.limit] - Page size (default: 200, max: 500)
   * @param {string} [params.lastKey] - JSON-encoded DynamoDB pagination token from previous response
   * @param {string} [params.openingDate] - Opening date in format YYYY-MM-DD (e.g., '2025-11-01') for opening stock calculation
   * @param {string} [params.date] - Display date label for the report (optional)
   * @returns {Promise<Object>} Object containing success status, summary, items, and pagination info
   * 
   * Response includes:
   * - summary: { total_items, total_quantity, low_stock_items, report_date, opening_quantity?, opening_value?, opening_date? }
   * - items: Array of stock items with current_quantity (closing stock) and optional opening_quantity/opening_value
   * - last_key: Pagination token (null when no more results)
   */
  getClosingStock: async ({
    limit,
    lastKey,
    openingDate,
    date,
  } = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey !== undefined && lastKey !== null) {
        queryParams.append('last_key', lastKey);
      }
      if (openingDate !== undefined && openingDate !== null) {
        queryParams.append('opening_date', openingDate);
      }
      if (date !== undefined && date !== null) {
        queryParams.append('date', date);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/closing${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      // Transform response to include all documented fields
      // Note: Include ALL items, even those with zero quantity, as they are valid stock items
      const transformedItems = (responseData.items || []).map(item => ({
        stock_item_id: item.stock_item_id,
        tenant_id: item.tenant_id,
        item_name: item.item_name,
        unit_of_measure: item.unit_of_measure,
        current_quantity: item.current_quantity, // This is the closing stock
        reorder_threshold: item.reorder_threshold,
        reorder_quantity: item.reorder_quantity,
        vendor_name: item.vendor_name,
        site_id: item.site_id,
        site_name: item.site_name,
        price_per_unit: item.price_per_unit,
        is_active: item.is_active,
        is_consumable: item.is_consumable,
        description: item.description,
        // Opening stock fields (only if opening_date was provided)
        opening_quantity: item.opening_quantity,
        opening_value: item.opening_value,
      }));

      return {
        success: true,
        data: responseData,
        summary: responseData.summary || null,
        items: transformedItems,
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Closing Stock API: Error in getClosingStock:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          summary: null,
          items: [],
          lastKey: null,
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          summary: null,
          items: [],
          lastKey: null,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch closing stock',
        summary: null,
        items: [],
        lastKey: null,
      };
    }
  },

  /**
   * Creates a closing stock snapshot for a specific date.
   * POST /stock/closing
   * Lambda: innoworx-stock-create-closing
   * 
   * @param {Object} closingStockData - Closing stock snapshot data
   * @param {string} closingStockData.closing_date - Closing date in format YYYY-MM-DD (required)
   * @param {Array} closingStockData.items - Array of stock items with closing quantities (required)
   * @param {string} [closingStockData.notes] - Optional notes for the snapshot
   * @param {string} [closingStockData.site_id] - Optional site identifier
   * @param {string} [closingStockData.site_name] - Optional site name
   * @returns {Promise<Object>} Object containing success status and created snapshot data
   * 
   * Item structure:
   * - stock_item_id (required)
   * - closing_quantity (required, must be >= 0)
   * - item_name, unit_of_measure, reorder_threshold, price_per_unit, vendor_name, site_id, site_name (optional)
   */
  createClosingStock: async (closingStockData) => {
    try {
      // Validate required fields
      if (!closingStockData.closing_date) {
        throw new Error('closing_date is required');
      }
      if (!closingStockData.items || !Array.isArray(closingStockData.items) || closingStockData.items.length === 0) {
        throw new Error('items array is required and must not be empty');
      }

      // Validate each item
      closingStockData.items.forEach((item, index) => {
        if (!item.stock_item_id) {
          throw new Error(`Item at index ${index}: stock_item_id is required`);
        }
        if (item.closing_quantity === undefined || item.closing_quantity === null) {
          throw new Error(`Item at index ${index}: closing_quantity is required`);
        }
        if (parseFloat(item.closing_quantity) < 0) {
          throw new Error(`Item at index ${index}: closing_quantity must be >= 0`);
        }
      });

      // Prepare request body according to API documentation
      const requestBody = {
        closing_date: closingStockData.closing_date,
        items: closingStockData.items.map(item => ({
          stock_item_id: item.stock_item_id,
          closing_quantity: parseFloat(item.closing_quantity) || 0,
          // Optional fields
          ...(item.item_name && { item_name: item.item_name }),
          ...(item.unit_of_measure && { unit_of_measure: item.unit_of_measure }),
          ...(item.reorder_threshold !== undefined && { reorder_threshold: parseFloat(item.reorder_threshold) || 0 }),
          ...(item.price_per_unit !== undefined && { price_per_unit: parseFloat(item.price_per_unit) || 0 }),
          ...(item.vendor_name && { vendor_name: item.vendor_name }),
          // Include closing_reason if it exists and is not empty (after trimming)
          ...(item.closing_reason !== undefined && item.closing_reason !== null && String(item.closing_reason).trim() !== '' && { closing_reason: String(item.closing_reason).trim() }),
          ...(item.site_id && { site_id: item.site_id }),
          ...(item.site_name && { site_name: item.site_name }),
        })),
        ...(closingStockData.notes && { notes: closingStockData.notes }),
        ...(closingStockData.site_id && { site_id: closingStockData.site_id }),
        ...(closingStockData.site_name && { site_name: closingStockData.site_name }),
      };

      const response = await makeApiRequest('/stock/closing', 'POST', requestBody);
      
      let responseData = {};
      try {
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Could not parse response as JSON:', e);
        throw new Error('Invalid response format from server');
      }

      // Log closing_reason from response for debugging
      if (responseData.items && Array.isArray(responseData.items) && responseData.items.length > 0) {
        console.log('Closing Stock API: Create response - first item closing_reason:', responseData.items[0].closing_reason);
      }

      return {
        success: true,
        data: responseData,
        closing_stock_id: responseData.closing_stock_id || null,
        closing_date: responseData.closing_date || closingStockData.closing_date,
        summary: responseData.summary || null,
      };
    } catch (error) {
      console.error('Closing Stock API: Error in createClosingStock:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        const errorData = error.responseData || {};
        // For 502 errors, provide more context
        if (error.status === 502) {
          return {
            success: false,
            error: 'Bad Gateway',
            message: 'The backend service is unavailable or experiencing issues. This could be due to:\n' +
                     '1. Lambda function timeout\n' +
                     '2. Lambda function error\n' +
                     '3. API Gateway configuration issue\n\n' +
                     'Please try again in a few moments or contact the backend team.',
            status: error.status,
            details: errorData,
          };
        }
        return {
          success: false,
          error: errorData.error || 'API Error',
          message: errorData.message || error.message || `Request failed with status ${error.status}`,
          status: error.status,
          details: errorData,
        };
      }

      return {
        success: false,
        error: 'Validation error',
        message: error.message || 'Failed to create closing stock snapshot',
      };
    }
  },


  /**
   * Creates a purchase order with line items.
   * POST /stock/purchase-orders
   * @param {Object} purchaseOrderData - Purchase order data
   * @param {string} purchaseOrderData.vendor_id - Vendor identifier (required)
   * @param {string} [purchaseOrderData.po_id] - Purchase order identifier (auto-generated if not provided)
   * @param {string} [purchaseOrderData.po_number] - Purchase order number (auto-generated if not provided)
   * @param {string} [purchaseOrderData.po_date] - PO date in format YYYY-MM-DD (default: current date)
   * @param {string} [purchaseOrderData.expected_delivery_date] - Expected delivery date in format YYYY-MM-DD
   * @param {string} [purchaseOrderData.status] - PO status: draft (default), approved, issued, closed, cancelled
   * @param {string} [purchaseOrderData.payment_status] - Payment status: unpaid (default), partial, paid
   * @param {string} [purchaseOrderData.currency] - Currency code (default: INR)
   * @param {string} [purchaseOrderData.fob] - Free on Board location
   * @param {Object} [purchaseOrderData.billing_address] - Billing address object
   * @param {Object} [purchaseOrderData.shipping_address] - Shipping address object
   * @param {string} [purchaseOrderData.payment_terms] - Payment terms (e.g., "Net 30")
   * @param {number} [purchaseOrderData.freight_total] - Freight charges (default: 0)
   * @param {number} [purchaseOrderData.freight_charges] - Alternative field name for freight_total
   * @param {string} [purchaseOrderData.freight_notes] - Notes about freight
   * @param {string} [purchaseOrderData.notes] - General notes
   * @param {Array} purchaseOrderData.lines - Array of line items (required)
   * @returns {Promise<Object>} Object containing success status and created purchase order data
   */
  createPurchaseOrder: async (purchaseOrderData) => {
    try {
      // Validate required fields
      if (!purchaseOrderData.vendor_id) {
        throw new Error('vendor_id is required');
      }
      if (!purchaseOrderData.lines || !Array.isArray(purchaseOrderData.lines) || purchaseOrderData.lines.length === 0) {
        throw new Error('lines array is required and must not be empty');
      }

      // Normalize line items - handle alternative field names
      const normalizedLines = purchaseOrderData.lines.map((line, index) => {
        const normalizedLine = { ...line };
        
        // Handle alternative field names
        if (!normalizedLine.item_id && normalizedLine.sku) {
          // If only sku is provided, use it as item_id
          normalizedLine.item_id = normalizedLine.sku;
        }
        if (!normalizedLine.description && normalizedLine.item_name) {
          normalizedLine.description = normalizedLine.item_name;
        }
        if (!normalizedLine.quantity && normalizedLine.quantity_ordered !== undefined) {
          normalizedLine.quantity = normalizedLine.quantity_ordered;
        }
        if (!normalizedLine.unit_cost && normalizedLine.rate !== undefined) {
          normalizedLine.unit_cost = normalizedLine.rate;
        }
        if (!normalizedLine.line_id) {
          normalizedLine.line_id = String(index + 1).padStart(3, '0');
        }

        return normalizedLine;
      });

      // Prepare request body
      const requestBody = {
        vendor_id: purchaseOrderData.vendor_id,
        ...(purchaseOrderData.po_id && { po_id: purchaseOrderData.po_id }),
        ...(purchaseOrderData.po_number && { po_number: purchaseOrderData.po_number }),
        ...(purchaseOrderData.po_date && { po_date: purchaseOrderData.po_date }),
        ...(purchaseOrderData.expected_delivery_date && { expected_delivery_date: purchaseOrderData.expected_delivery_date }),
        ...(purchaseOrderData.status && { status: purchaseOrderData.status }),
        ...(purchaseOrderData.payment_status && { payment_status: purchaseOrderData.payment_status }),
        ...(purchaseOrderData.currency && { currency: purchaseOrderData.currency }),
        ...(purchaseOrderData.fob && { fob: purchaseOrderData.fob }),
        ...(purchaseOrderData.billing_address && { billing_address: purchaseOrderData.billing_address }),
        ...(purchaseOrderData.shipping_address && { shipping_address: purchaseOrderData.shipping_address }),
        ...(purchaseOrderData.payment_terms && { payment_terms: purchaseOrderData.payment_terms }),
        ...(purchaseOrderData.freight_total !== undefined && { freight_total: purchaseOrderData.freight_total }),
        ...(purchaseOrderData.freight_charges !== undefined && { freight_total: purchaseOrderData.freight_charges }),
        ...(purchaseOrderData.freight_notes && { freight_notes: purchaseOrderData.freight_notes }),
        ...(purchaseOrderData.notes && { notes: purchaseOrderData.notes }),
        lines: normalizedLines,
      };


      const response = await makeApiRequest('/stock/purchase-orders', 'POST', requestBody);
      const responseData = await response.json();


      return {
        success: true,
        data: responseData,
        purchaseOrder: responseData.purchase_order || null,
        lines: responseData.lines || [],
      };
    } catch (error) {
      console.error('Closing Stock API: Error in createPurchaseOrder:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          purchaseOrder: null,
          lines: [],
        };
      }

      if (error.status) {
        const errorData = error.responseData || {};
        return {
          success: false,
          error: errorData.error || 'API Error',
          message: errorData.message || error.message || `Request failed with status ${error.status}`,
          purchaseOrder: null,
          lines: [],
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create purchase order',
        purchaseOrder: null,
        lines: [],
      };
    }
  },

  /**
   * Creates an invoice with line items.
   * POST /billing/invoices
   * @param {Object} invoiceData - Invoice data
   * @param {string} invoiceData.customer_id - Customer identifier (required, must exist in customers table)
   * @param {string} [invoiceData.invoice_id] - Invoice identifier (auto-generated if not provided)
   * @param {string} [invoiceData.invoice_number] - Invoice number (auto-generated if not provided)
   * @param {string} [invoiceData.invoice_date] - Invoice date in format YYYY-MM-DD (default: current date)
   * @param {string} [invoiceData.due_date] - Due date in format YYYY-MM-DD
   * @param {string} [invoiceData.status] - Invoice status: draft (default), sent, paid, overdue, cancelled
   * @param {string} [invoiceData.payment_status] - Payment status: unpaid (default), partial, paid
   * @param {string} [invoiceData.currency] - Currency code (default: INR)
   * @param {Object} [invoiceData.billing_address] - Billing address (uses customer's if not provided)
   * @param {Object} [invoiceData.shipping_address] - Shipping address (uses customer's if not provided)
   * @param {string} [invoiceData.payment_terms] - Payment terms (uses customer's if not provided)
   * @param {number} [invoiceData.shipping_total] - Shipping charges (default: 0)
   * @param {number} [invoiceData.shipping_charges] - Alternative field name for shipping_total
   * @param {number} [invoiceData.freight_total] - Alternative field name for shipping_total
   * @param {string} [invoiceData.shipping_notes] - Notes about shipping
   * @param {string} [invoiceData.notes] - General notes
   * @param {Array} invoiceData.lines - Array of line items (required)
   * @returns {Promise<Object>} Object containing success status and created invoice data
   */
  createInvoice: async (invoiceData) => {
    try {
      // Validate required fields
      if (!invoiceData.customer_id) {
        throw new Error('customer_id is required');
      }
      if (!invoiceData.lines || !Array.isArray(invoiceData.lines) || invoiceData.lines.length === 0) {
        throw new Error('lines array is required and must not be empty');
      }

      // Normalize line items - handle alternative field names
      const normalizedLines = invoiceData.lines.map((line, index) => {
        const normalizedLine = { ...line };
        
        // Handle alternative field names
        if (!normalizedLine.item_id && normalizedLine.sku) {
          normalizedLine.item_id = normalizedLine.sku;
        }
        if (!normalizedLine.description && normalizedLine.item_name) {
          normalizedLine.description = normalizedLine.item_name;
        }
        if (!normalizedLine.quantity && normalizedLine.quantity_ordered !== undefined) {
          normalizedLine.quantity = normalizedLine.quantity_ordered;
        }
        if (!normalizedLine.unit_price) {
          if (normalizedLine.rate !== undefined) {
            normalizedLine.unit_price = normalizedLine.rate;
          } else if (normalizedLine.unit_cost !== undefined) {
            normalizedLine.unit_price = normalizedLine.unit_cost;
          }
        }
        if (!normalizedLine.line_id) {
          normalizedLine.line_id = String(index + 1).padStart(3, '0');
        }

        return normalizedLine;
      });

      // Prepare request body
      const requestBody = {
        customer_id: invoiceData.customer_id,
        ...(invoiceData.invoice_id && { invoice_id: invoiceData.invoice_id }),
        ...(invoiceData.invoice_number && { invoice_number: invoiceData.invoice_number }),
        ...(invoiceData.invoice_date && { invoice_date: invoiceData.invoice_date }),
        ...(invoiceData.due_date && { due_date: invoiceData.due_date }),
        ...(invoiceData.status && { status: invoiceData.status }),
        ...(invoiceData.payment_status && { payment_status: invoiceData.payment_status }),
        ...(invoiceData.currency && { currency: invoiceData.currency }),
        ...(invoiceData.billing_address && { billing_address: invoiceData.billing_address }),
        ...(invoiceData.shipping_address && { shipping_address: invoiceData.shipping_address }),
        ...(invoiceData.payment_terms && { payment_terms: invoiceData.payment_terms }),
        ...(invoiceData.shipping_total !== undefined && { shipping_total: invoiceData.shipping_total }),
        ...(invoiceData.shipping_charges !== undefined && { shipping_total: invoiceData.shipping_charges }),
        ...(invoiceData.freight_total !== undefined && { shipping_total: invoiceData.freight_total }),
        ...(invoiceData.shipping_notes && { shipping_notes: invoiceData.shipping_notes }),
        ...(invoiceData.notes && { notes: invoiceData.notes }),
        lines: normalizedLines,
      };


      const response = await makeApiRequest('/billing/invoices', 'POST', requestBody);
      const responseData = await response.json();


      return {
        success: true,
        data: responseData,
        invoice: responseData.invoice || null,
        lines: responseData.lines || [],
      };
    } catch (error) {
      console.error('Closing Stock API: Error in createInvoice:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          invoice: null,
          lines: [],
        };
      }

      if (error.status) {
        const errorData = error.responseData || {};
        return {
          success: false,
          error: errorData.error || 'API Error',
          message: errorData.message || error.message || `Request failed with status ${error.status}`,
          invoice: null,
          lines: [],
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to create invoice',
        invoice: null,
        lines: [],
      };
    }
  },

  /**
   * Lists closing stock snapshots with flexible filtering options.
   * GET /stock/closing/snapshots
   * Lambda: innoworx-stock-list-closing
   * 
   * @param {Object} params - Optional parameters for filtering and pagination
   * @param {string} [params.closing_date] - Filter by specific date (YYYY-MM-DD)
   * @param {string} [params.start_date] - Filter from this date (YYYY-MM-DD)
   * @param {string} [params.end_date] - Filter up to this date (YYYY-MM-DD)
   * @param {string} [params.site_id] - Filter by site
   * @param {number} [params.limit] - Page size (default: 200, max: 500)
   * @param {string} [params.last_key] - Pagination token from previous response
   * @returns {Promise<Object>} Object containing success status, snapshots array, and pagination info
   */
  listClosingStockSnapshots: async ({
    closing_date,
    start_date,
    end_date,
    site_id,
    limit,
    last_key,
  } = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (closing_date !== undefined && closing_date !== null) {
        queryParams.append('closing_date', closing_date);
      }
      if (start_date !== undefined && start_date !== null) {
        queryParams.append('start_date', start_date);
      }
      if (end_date !== undefined && end_date !== null) {
        queryParams.append('end_date', end_date);
      }
      if (site_id !== undefined && site_id !== null) {
        queryParams.append('site_id', site_id);
      }
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (last_key !== undefined && last_key !== null) {
        queryParams.append('last_key', last_key);
      }

      const queryString = queryParams.toString();
      const endpoint = `/stock/closing/snapshots${queryString ? `?${queryString}` : ''}`;

      console.log(`Closing Stock API: Fetching closing stock snapshots with params:`, {
        closing_date,
        start_date,
        end_date,
        site_id,
        limit,
        last_key,
      });

      let response;
      try {
        response = await makeApiRequest(endpoint, 'GET');
      } catch (fetchError) {
        // Handle CORS and network errors specifically
        if (fetchError.message && (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('CORS'))) {
          console.error('Closing Stock API: CORS or network error - this is typically a server configuration issue');
          return {
            success: false,
            error: 'Network Error',
            message: 'Unable to connect to the API. This may be due to CORS configuration or network issues. Please contact support if this persists.',
            snapshots: [],
            lastKey: null,
          };
        }
        throw fetchError;
      }

      const responseData = await response.json();

      // Log closing_reason from response for debugging
      if (responseData.snapshots && responseData.snapshots.length > 0) {
        const firstSnapshot = responseData.snapshots[0];
        if (firstSnapshot.items && firstSnapshot.items.length > 0) {
          const firstItem = firstSnapshot.items[0];
          console.log('Closing Stock API: First item closing_reason:', firstItem.closing_reason);
          console.log('Closing Stock API: First item has closing_reason?', 'closing_reason' in firstItem);
        }
      }

      return {
        success: true,
        data: responseData,
        snapshots: responseData.snapshots || [],
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Closing Stock API: Error in listClosingStockSnapshots:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          snapshots: [],
          lastKey: null,
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
          snapshots: [],
          lastKey: null,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch closing stock snapshots',
        snapshots: [],
        lastKey: null,
      };
    }
  },
};

